/**
 * AuthorizationContext
 *
 * React Context that eagerly fetches the current user's allowed actions
 * once authenticated, and stores them as a Set for O(1) lookups.
 *
 * Provides helper methods for permission checks that mirror the backend
 * `require_actions` decorator DSL (AND / OR logic).
 *
 * Must be nested inside `AuthenticationProvider`.
 */

import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from 'react'
import { httpClient } from '@/lib/http-client'
import { useAuthentication } from '@/features/auth/AuthenticationContext'

// =============================================================================
// Types
// =============================================================================

interface AuthorizationState {
    /** All action keys the current user is allowed to perform. */
    actions: Set<string>
    /** The user's roles as returned by the authorization endpoint. */
    roles: string[]
    /** True while the actions are being fetched. */
    isLoading: boolean
}

interface AuthorizationHelpers {
    /** Check if the user has a specific action. */
    hasAction: (action: string) => boolean
    /** Check if the user has ALL of the specified actions (AND). */
    hasAllActions: (...actions: string[]) => boolean
    /** Check if the user has ANY of the specified actions (OR). */
    hasAnyAction: (...actions: string[]) => boolean
    /**
     * Evaluate a backend-style expression:
     *  - String args are AND-ed.
     *  - Array args are OR groups (at least one must match).
     *
     * Example: `evaluate('view_org', ['edit_org', 'admin_org'])`
     *   → user must have `view_org` AND (`edit_org` OR `admin_org`)
     */
    evaluate: (...expression: (string | string[])[]) => boolean
    /** Force a re-fetch of user actions. */
    refetchActions: () => Promise<void>
}

export type AuthorizationContextValue = AuthorizationState & AuthorizationHelpers

// =============================================================================
// Context
// =============================================================================

const AuthorizationContext = createContext<AuthorizationContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

interface AuthorizationProviderProps {
    children: React.ReactNode
}

export function AuthorizationProvider({ children }: AuthorizationProviderProps) {
    const { isAuthenticated, isLoading: authLoading, user } = useAuthentication()

    const isAdmin = user?.is_administrator === true

    const [actionList, setActionList] = useState<string[]>([])
    const [roles, setRoles] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // ── Derive a Set for O(1) lookups ────────────────────────────────
    const actions = useMemo(() => new Set(actionList), [actionList])

    // ── Fetch actions ─────────────────────────────────────────────────
    const fetchActions = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await httpClient.get<{ roles: string[]; actions: string[] }>(
                '/authorization/me/actions',
            )
            setActionList(res.data.actions)
            setRoles(res.data.roles ?? [])
        } catch {
            console.error('Failed to fetch user actions')
            setActionList([])
            setRoles([])
        } finally {
            setIsLoading(false)
        }
    }, [])

    // ── Fetch on authentication change ────────────────────────────────
    useEffect(() => {
        if (authLoading) return // wait until auth is settled

        if (isAuthenticated) {
            fetchActions()
        } else {
            // User logged out — clear authorization state
            setActionList([])
            setRoles([])
            setIsLoading(false)
        }
    }, [isAuthenticated, authLoading, fetchActions])

    // ── Helpers ───────────────────────────────────────────────────────
    const hasAction = useCallback(
        (action: string) => isAdmin || actions.has(action),
        [actions, isAdmin],
    )

    const hasAllActions = useCallback(
        (...acts: string[]) => isAdmin || acts.every((a) => actions.has(a)),
        [actions, isAdmin],
    )

    const hasAnyAction = useCallback(
        (...acts: string[]) => isAdmin || acts.some((a) => actions.has(a)),
        [actions, isAdmin],
    )

    const evaluate = useCallback(
        (...expression: (string | string[])[]) => {
            if (isAdmin) return true
            for (const element of expression) {
                if (Array.isArray(element)) {
                    if (!element.some((a) => actions.has(a))) return false
                } else {
                    if (!actions.has(element)) return false
                }
            }
            return true
        },
        [actions, isAdmin],
    )

    // ── Value ─────────────────────────────────────────────────────────
    const value: AuthorizationContextValue = {
        actions,
        roles,
        isLoading,
        hasAction,
        hasAllActions,
        hasAnyAction,
        evaluate,
        refetchActions: fetchActions,
    }

    return (
        <AuthorizationContext.Provider value={value}>
            {children}
        </AuthorizationContext.Provider>
    )
}

// =============================================================================
// Hook
// =============================================================================

export function useAuthorization(): AuthorizationContextValue {
    const ctx = useContext(AuthorizationContext)
    if (!ctx) {
        throw new Error('useAuthorization must be used within an AuthorizationProvider')
    }
    return ctx
}
